/**
 * Decode the permissions string that the backend ships in the login response.
 *
 * Backend encoding pipeline (for reference):
 *   const buffer = JSON.stringify({ permissions: allPermissions });
 *   const zipped = zlib.gzipSync(buffer);
 *   const encoded = zipped.toString('base64');
 *
 * Frontend decoding (this function): base64 → bytes → gunzip → JSON.parse →
 * extract `.permissions`. Uses native `DecompressionStream`, so requires
 * Chrome 80+, Firefox 113+, Safari 16.4+ (all GA since early 2023).
 */
export const decodePermissions = async (encoded: string | null | undefined): Promise<string[]> => {
  if (!encoded) return [];

  try {
    const binaryString = atob(encoded);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const stream = new Blob([bytes as unknown as BlobPart]).stream().pipeThrough(
      new DecompressionStream('gzip')
    );
    const text = await new Response(stream).text();
    const parsed = JSON.parse(text);

    if (parsed && Array.isArray(parsed.permissions)) {
      return parsed.permissions;
    }
    return [];
  } catch (err) {
    console.error('Failed to decode permissions payload:', err);
    return [];
  }
};

export const hasPermission = (permissions: string[] | null | undefined, required: string): boolean => {
  if (!permissions || permissions.length === 0) return false;
  return permissions.includes(required);
};
